'use client';

import {
  formatResolutionCountdown,
  remainingResolutionSeconds,
} from '@morfi/backend-sdk-v2/client';
import { AnimatePresence, domMax, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import {
  PaintedMarketShareLeaf,
  usePaintedMarketShareDisplayStore,
} from '@/components/chart/painted-market-share-display';
import { OptimizedImage } from '@/components/images/optimized-image';
import { CardSectionLabel } from '@/components/ui/card-section-label';
import { Panel } from '@/components/ui/panel';
import { onEvent } from '@/events';
import { outcomeHueToStepVar } from '@/hooks/chart/use-outcome-color';
import { formatPercentValue } from '@/lib/chart/format';
import type { PaintedMarketShareSnapshot } from '@/lib/chart/painted-market-share-display';
import type {
  ResolutionCardOutcome,
  ResolutionCardState,
} from '@/lib/market/resolution-card-state';
import { MOTION_ENTRANCE, MOTION_EXIT } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

export type { ResolutionCardOutcome, ResolutionCardState };

type Direction = 'sooner' | 'later';

const MS_PER_SECOND: number = 1000;
const URGENT_SECONDS: number = 60;
/** After a trade the digits roll from the old time to the new one over this long. */
const ROLL_MS: number = 700;
/** The direction colour stays on the digits this long after the roll settles. */
const HOLD_MS: number = 900;
const THUMB_SIZE_CLASS: string = 'size-[46px]';
// Presence — the same spring and reduced-motion fallbacks as the Position
// card's quick-trade chips, so the card arrives and leaves the way they do.
const REVEAL_SPRING = { type: 'spring' as const, visualDuration: 0.12, bounce: 0.18 };
const REVEAL_SPRING_DELAYED = {
  type: 'spring' as const,
  visualDuration: 0.12,
  bounce: 0.18,
  delay: 0.05,
};
const REVEAL_INITIAL = { opacity: 0 } as const;
const REVEAL_ANIMATE = { opacity: 1 } as const;
const REVEAL_EXIT = { opacity: 0, transition: REVEAL_SPRING } as const;
const REVEAL_REDUCED = { duration: 0 } as const;
const REVEAL_EXIT_REDUCED = { opacity: 0 } as const;
// Trade delta readout beside the digits: slides in from the right with the
// roll and fades once the digits settle.
const DELTA_INITIAL = { opacity: 0, x: 4 } as const;
const DELTA_ANIMATE = { opacity: 1, x: 0 } as const;
const DELTA_EXIT = { opacity: 0, transition: MOTION_EXIT } as const;
const DELTA_EXIT_REDUCED = { opacity: 0, transition: REVEAL_REDUCED } as const;

/**
 * Resolution status card between the chart and the Outcomes strip, built
 * exactly like the Position card: a section label with a live dot, the 46px
 * outcome thumbnail with its hue dot, and one value row. Two states:
 *
 * - `projected`: the outcome on course to cross the threshold first, how far
 *   its share is from the line, and a countdown to the projected crossing.
 *   `crossAtMs` is fixed between trades (see `projectMarketResolutionAt`), so the digits
 *   only move for a trade — rolling up or down in the profit / loss colour —
 *   and the one-second tick lives in the countdown leaf alone.
 * - `resolving`: the outcome reached the line; the crank settles the market.
 * - `graduating`: the resolution landed; the page holds until the settled
 *   page's data is in. For both, the section label names the step and the
 *   countdown stays on the right, held at 0:00 in the warning tone, so the
 *   time never swaps out for something else.
 *
 * Pass `null` while there is nothing to show: the card stays mounted and
 * fades out through the same presence block the Position card's chips use
 * (`AnimatePresence initial={false}` — no animation on first paint, only
 * when the card appears or goes away; a projected → resolving change updates
 * the card in place so the countdown keeps running).
 */
export function ResolutionCard({
  state,
  painted = false,
}: {
  state: ResolutionCardState | null;
  painted?: boolean;
}): React.ReactNode {
  const prefersReduced = useReducedMotion() === true;
  return (
    <LazyMotion features={domMax}>
      <AnimatePresence initial={false} mode="popLayout">
        {state !== null ? (
          <m.div
            key="resolution-card"
            layout
            transition={prefersReduced ? REVEAL_REDUCED : REVEAL_SPRING_DELAYED}
            initial={prefersReduced ? false : REVEAL_INITIAL}
            animate={REVEAL_ANIMATE}
            exit={prefersReduced ? REVEAL_EXIT_REDUCED : REVEAL_EXIT}
          >
            <ResolutionCardBody state={state} painted={painted} />
          </m.div>
        ) : null}
      </AnimatePresence>
    </LazyMotion>
  );
}

function ResolutionCardBody({
  state,
  painted,
}: {
  state: ResolutionCardState;
  painted: boolean;
}): React.ReactNode {
  const t = useTranslations('chart.resolution_card');
  const tMarket = useTranslations('market');
  const unavailableText = tMarket('backers.earnings_unavailable');
  const { outcome } = state;
  const projectedCrossAtMs = state.kind === 'projected' ? state.crossAtMs : null;
  // Server-derived text, rendered in SSR HTML and until the first painted frame.
  const serverText =
    state.kind === 'projected'
      ? t('from_resolution', { gap: formatPercentValue(state.gap, 2) })
      : t('reached_line', { threshold: formatPercentValue(state.threshold, 0) });

  // Analytics: fire once per projected mount. Refs are imperative state, not
  // memoization (FQ-140 compliant). Re-entry creates a fresh ref, so the
  // event refires naturally on the next mount.
  const firedRef = useRef(false);
  useEffect((): void => {
    if (projectedCrossAtMs === null || firedRef.current) return;
    firedRef.current = true;
    onEvent('market_approaching_resolution_viewed', {
      marketPubkey: outcome.marketPubkey,
      outcomeIndex: outcome.outcomeIndex,
      secondsToResolve: remainingResolutionSeconds(projectedCrossAtMs, Date.now()),
    });
  }, [projectedCrossAtMs, outcome.marketPubkey, outcome.outcomeIndex]);

  return (
    <Panel className="flex flex-col gap-0 overflow-hidden p-0">
      <CardSectionLabel
        className="px-4 pt-3 pb-2.5"
        afterTitle={<LiveDot tone={state.kind === 'projected' ? 'success' : 'warning'} />}
      >
        {state.kind === 'projected'
          ? t('projected_head')
          : state.kind === 'resolving'
            ? t('resolving')
            : t('graduating')}
      </CardSectionLabel>
      <div className="border-t border-border-primary px-4 pt-3.5 pb-4">
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3.5">
          <OutcomeThumb outcome={outcome} />
          <div className="min-w-0">
            <div className="truncate text-body-lg font-semibold tracking-tight text-foreground-primary">
              {outcome.label}
            </div>
            <div className="mt-0.5 text-body text-foreground-tertiary leading-tight tabular-nums">
              {painted ? (
                <PaintedMarketShareLeaf
                  fallback={serverText}
                  format={(snapshot: PaintedMarketShareSnapshot) => {
                    const threshold = snapshot.threshold;
                    const paintedValue = snapshot.values[outcome.outcomeIndex];
                    if (threshold === null) return { text: unavailableText };
                    if (state.kind !== 'projected') {
                      return {
                        text: t('reached_line', {
                          threshold: formatPercentValue(threshold, 0),
                        }),
                      };
                    }
                    if (typeof paintedValue !== 'number' || !Number.isFinite(paintedValue)) {
                      return { text: unavailableText };
                    }
                    return {
                      text: t('from_resolution', {
                        gap: formatPercentValue(Math.max(0, threshold - paintedValue), 2),
                      }),
                    };
                  }}
                />
              ) : (
                serverText
              )}
            </div>
          </div>
          <div className="min-w-0 text-right">
            <div className="text-body text-foreground-tertiary leading-tight">
              {t('resolves_in')}
            </div>
            {painted ? (
              <PaintedResolutionCountdown
                crossAtMs={state.crossAtMs}
                settled={state.kind !== 'projected'}
              />
            ) : (
              <ResolutionCountdown
                crossAtMs={state.crossAtMs}
                settled={state.kind !== 'projected'}
              />
            )}
          </div>
        </div>
      </div>
    </Panel>
  );
}

function PaintedResolutionCountdown({
  crossAtMs,
  settled,
}: {
  readonly crossAtMs: number;
  readonly settled: boolean;
}): React.ReactNode {
  const store = usePaintedMarketShareDisplayStore();
  // Keep the SSR countdown until the first frame, then follow the chart's
  // presentation head. Whole-second updates avoid a React render per frame.
  const [paintedNowMs, setPaintedNowMs] = useState(
    () => store.getPaintSnapshot()?.displayedHeadTimeMs ?? Date.now(),
  );
  useEffect(() => {
    return store.subscribePaint(() => {
      const headMs = store.getPaintSnapshot()?.displayedHeadTimeMs;
      if (headMs === undefined) return;
      setPaintedNowMs((current) =>
        remainingResolutionSeconds(crossAtMs, current) ===
        remainingResolutionSeconds(crossAtMs, headMs)
          ? current
          : headMs,
      );
    });
  }, [store, crossAtMs]);
  return (
    <ResolutionCountdown crossAtMs={crossAtMs} settled={settled} paintedNowMs={paintedNowMs} />
  );
}

function LiveDot({ tone }: { tone: 'success' | 'warning' }): React.ReactNode {
  const fill = tone === 'success' ? 'bg-feedback-success-accent' : 'bg-feedback-warning-accent';
  return (
    <span aria-hidden className="relative inline-flex size-2">
      <span
        className={cn(
          'absolute inset-0 animate-ping rounded-full opacity-60 motion-reduce:animate-none',
          fill,
        )}
      />
      <span className={cn('absolute inset-0 m-auto size-1.5 rounded-full', fill)} />
    </span>
  );
}

function OutcomeThumb({ outcome }: { outcome: ResolutionCardOutcome }): React.ReactNode {
  return (
    <div className={cn('relative shrink-0', THUMB_SIZE_CLASS)}>
      <OptimizedImage
        variant="thumbnail"
        entity={{
          kind: 'outcome',
          marketPubkey: outcome.marketPubkey,
          outcomeIndex: outcome.outcomeIndex,
          hue: outcome.hue,
          imageUrl: outcome.imageUrl,
          nsfw: outcome.imageNsfw,
        }}
        alt={outcome.label}
        size={THUMB_SIZE_CLASS}
        rounded="rounded-control"
        // ALLOW-NO-PREVIEW: outcome marker inside the resolution card; opening the lightbox would steal the card's tap target
        preview={false}
      />
      <span
        aria-hidden
        data-testid="resolution-hue-dot"
        className={cn(
          'absolute -right-1 -bottom-1 size-[13px] rounded-full border-2 border-background-primary',
        )}
        style={{ backgroundColor: outcomeHueToStepVar(outcome.hue, 400) }}
      />
    </div>
  );
}

interface TradeMove {
  /** Presence key — one readout per burst of trades, updated in place. */
  readonly key: number;
  readonly direction: Direction;
  /** Net signed change to the remaining time across the burst, in seconds. */
  readonly deltaSeconds: number;
}

/** The only part of the card that ticks. */
function ResolutionCountdown({
  crossAtMs,
  settled,
  paintedNowMs,
}: {
  crossAtMs: number;
  /** The line has been reached: hold at 0:00 and ignore crossing-time changes. */
  settled: boolean;
  paintedNowMs?: number;
}): React.ReactNode {
  const t = useTranslations('chart.resolution_card');
  const prefersReduced = useReducedMotion() === true;
  const [localNowMs, setLocalNowMs] = useState(() => Date.now());
  const nowMs = paintedNowMs ?? localNowMs;
  const nowRef = useRef(nowMs);
  const [rolled, setRolled] = useState<number | null>(null);
  const [trade, setTrade] = useState<TradeMove | null>(null);
  const shownRef = useRef<number | null>(null);
  const crossAtRef = useRef(crossAtMs);

  const seconds = rolled ?? remainingResolutionSeconds(crossAtMs, nowMs);

  useEffect(() => {
    if (paintedNowMs !== undefined) return;
    const id = setInterval(() => setLocalNowMs(Date.now()), MS_PER_SECOND);
    return (): void => clearInterval(id);
  }, [paintedNowMs]);

  useEffect(() => {
    nowRef.current = nowMs;
  }, [nowMs]);

  // A trade moved the crossing time: show how much, roll the digits from the
  // last painted value to the new one, colour both by direction, then settle.
  // A burst of trades folds into the one readout (net delta, same element, so
  // nothing re-mounts) and each new roll takes over from wherever the digits
  // are — the previous rolled value stays on screen until the next frame
  // overwrites it, so the target never flashes in between.
  useEffect(() => {
    if (crossAtRef.current === crossAtMs) return;
    const deltaSeconds = Math.round((crossAtMs - crossAtRef.current) / MS_PER_SECOND);
    crossAtRef.current = crossAtMs;
    if (settled) {
      // Re-running this effect cancelled the timer that clears the readout.
      setRolled(null);
      setTrade(null);
      return;
    }
    setTrade((current: TradeMove | null): TradeMove | null => {
      const total = (current?.deltaSeconds ?? 0) + deltaSeconds;
      if (total === 0) return null;
      return {
        key: current?.key ?? crossAtMs,
        direction: total < 0 ? 'sooner' : 'later',
        deltaSeconds: total,
      };
    });
    const hold = setTimeout(() => setTrade(null), ROLL_MS + HOLD_MS);
    const from = shownRef.current;
    if (prefersReduced || from === null) {
      setRolled(null);
      return (): void => clearTimeout(hold);
    }
    const startedAt = performance.now();
    let frame = requestAnimationFrame(function step(now: number): void {
      const progress = Math.min(1, (now - startedAt) / ROLL_MS);
      const target = remainingResolutionSeconds(crossAtMs, nowRef.current);
      setRolled(Math.round(from + (target - from) * easeOut(progress)));
      if (progress < 1) frame = requestAnimationFrame(step);
      else setRolled(null);
    });
    return (): void => {
      clearTimeout(hold);
      cancelAnimationFrame(frame);
    };
  }, [crossAtMs, settled, prefersReduced]);

  // The last painted value is where the next trade's roll starts from. This
  // must run AFTER the trade effect above: effects fire in declaration order,
  // and the trade effect needs the value painted before the crossing time
  // changed, not the one just computed from the new target.
  useEffect(() => {
    shownRef.current = seconds;
  });

  const clock = (total: number): string => {
    const formatted = formatResolutionCountdown(total);
    switch (formatted.kind) {
      case 'days':
        return t('countdown_days', { days: formatted.days, hours: formatted.hours });
      case 'clock':
        return formatted.text;
      default: {
        const exhaustive: never = formatted;
        return exhaustive;
      }
    }
  };

  const direction = trade?.direction ?? null;
  const urgent = seconds <= URGENT_SECONDS;
  const tone =
    direction === 'sooner'
      ? 'text-data-profit'
      : direction === 'later'
        ? 'text-data-loss'
        : urgent
          ? 'text-feedback-warning-content'
          : 'text-foreground-primary';

  // The server and the client paint a different second; React reconciles the
  // text without a hydration warning.
  return (
    <div className="relative mt-0.5 inline-block">
      <AnimatePresence initial={false}>
        {trade !== null ? (
          <m.span
            key={trade.key}
            data-testid="resolution-delta"
            aria-hidden
            initial={prefersReduced ? false : DELTA_INITIAL}
            animate={DELTA_ANIMATE}
            exit={prefersReduced ? DELTA_EXIT_REDUCED : DELTA_EXIT}
            transition={prefersReduced ? REVEAL_REDUCED : MOTION_ENTRANCE}
            className={cn(
              'absolute top-1/2 right-full mr-1.5 -translate-y-1/2 whitespace-nowrap text-caption font-semibold tabular-nums',
              trade.direction === 'sooner' ? 'text-data-profit' : 'text-data-loss',
            )}
          >
            {`${trade.deltaSeconds < 0 ? '−' : '+'}${clock(Math.abs(trade.deltaSeconds))}`}
          </m.span>
        ) : null}
      </AnimatePresence>
      <div
        data-testid="resolution-countdown"
        suppressHydrationWarning
        className={cn(
          'text-heading-lg font-bold tabular-nums transition-colors',
          tone,
          urgent && direction === null && 'animate-pulse motion-reduce:animate-none',
        )}
      >
        {clock(seconds)}
      </div>
    </div>
  );
}

function easeOut(progress: number): number {
  return 1 - (1 - progress) ** 3;
}
